import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './i18n';
import '@/App.css';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import BillsPage from './pages/BillsPage';
import BillDetailPage from './pages/BillDetailPage';
import AppointmentsPage from './pages/AppointmentsPage';
import HistoryPage from './pages/HistoryPage';
import SettingsPage from './pages/SettingsPage';
import AddressChangePage from './pages/AddressChangePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import AdminPage from './pages/AdminPage';
import Layout from './components/Layout';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import SplashScreen from './components/SplashScreen';
import { Toaster } from 'sonner';

// Register Service Worker for PWA - with error handling for mobile
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then((registration) => {
        console.log('SHALEM PWA ready');
      })
      .catch((error) => {
        // Silently fail - app works without service worker
        console.log('PWA not available');
      });
  });
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [showSplash, setShowSplash] = useState(!sessionStorage.getItem('splash_shown'));
  const { i18n } = useTranslation();

  const handleSplashComplete = useCallback(() => {
    setShowSplash(false);
    sessionStorage.setItem('splash_shown', 'true');
  }, []);

  // Update document direction based on language
  useEffect(() => {
    const rtlLanguages = ['he', 'ar'];
    const isRtl = rtlLanguages.includes(i18n.language);
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedLanguage = localStorage.getItem('selected_language');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      // Use stored language preference (from login page) if available
      const language = storedLanguage || JSON.parse(storedUser).preferred_language || 'en';
      i18n.changeLanguage(language);
    }
  }, [i18n]);

  const handleLogin = (userData, userToken) => {
    // Keep the language that was selected on the login page
    const currentLanguage = i18n.language;
    
    // Update user data with the current language
    const updatedUser = { ...userData, preferred_language: currentLanguage };
    
    setUser(updatedUser);
    setToken(userToken);
    localStorage.setItem('token', userToken);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    localStorage.setItem('selected_language', currentLanguage);
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  if (!user || !token) {
    return (
      <>
        {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<AuthPage onLogin={handleLogin} />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-center" />
      </>
    );
  }

  return (
    <>
      <BrowserRouter>
        <Layout user={user} onLogout={handleLogout} token={token}>
          <Routes>
            <Route path="/" element={<Dashboard token={token} />} />
            <Route path="/bills" element={<BillsPage token={token} />} />
            <Route path="/bills/:billId" element={<BillDetailPage token={token} />} />
            <Route path="/appointments" element={<AppointmentsPage token={token} />} />
            <Route path="/history" element={<HistoryPage token={token} />} />
            <Route path="/address" element={<AddressChangePage token={token} />} />
            <Route path="/settings" element={<SettingsPage user={user} token={token} onUserUpdate={setUser} />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
        <PWAInstallPrompt />
      </BrowserRouter>
      <Toaster richColors position="top-center" />
    </>
  );
}

export default App;